import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { assetUrl } from "@/lib/api";

const reveal = {
  initial: { opacity: 0, y: 48 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

function ChapterHeading({ num, title, dark = true }) {
  return (
    <motion.div {...reveal} className="flex items-end gap-5 md:gap-8">
      <span className="font-serif font-light text-7xl md:text-8xl leading-[0.8] text-gold">{num}</span>
      <div className="pb-1">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-medium mb-2">Chapter {num}</p>
        <h3 className={`font-serif font-medium tracking-tight text-3xl md:text-5xl ${dark ? "text-ivory" : "text-charcoal"}`}>
          {title}
        </h3>
      </div>
    </motion.div>
  );
}

function ParallaxImage({ src, alt, className, testid }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className || ""}`}>
      <motion.img
        src={assetUrl(src)}
        alt={alt}
        data-testid={testid}
        style={{ y }}
        className="w-full h-full object-cover scale-[1.15]"
      />
    </div>
  );
}

export default function About({ about }) {
  const images = about.images || [];
  const audience = about.audience || [];
  const whyParagraphs = (about.why_exists || "").split("\n\n").filter(Boolean);

  return (
    <>
      <section id="about" data-testid="about-section" className="bg-charcoal text-ivory py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p {...reveal} className="text-xs uppercase tracking-[0.35em] text-gold font-medium mb-20">
            About — The Living Heritage
          </motion.p>

          <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
            <div className="lg:col-span-6">
              <ChapterHeading num="01" title="Who We Are" />
              <motion.p {...reveal} className="mt-10 text-ivory/75 text-base md:text-lg leading-relaxed max-w-xl" data-testid="about-short-description">
                {about.short_description}
              </motion.p>
            </div>
            <motion.div {...reveal} className="lg:col-span-6 relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50 pointer-events-none" aria-hidden="true" />
              <ParallaxImage
                src={images[0]}
                alt="Theyyam performer in ceremonial attire"
                testid="about-image-1"
                className="aspect-[4/5] max-h-[560px] w-full"
              />
            </motion.div>
          </div>

          <div className="mt-32 md:mt-40 grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <ChapterHeading num="02" title="Our Mission" />
            </div>
            <motion.blockquote {...reveal} className="lg:col-span-8" data-testid="about-mission">
              <span className="font-serif text-gold text-7xl leading-none block mb-2" aria-hidden="true">“</span>
              <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-snug text-ivory/90">
                {about.mission}
              </p>
            </motion.blockquote>
          </div>

          <div className="mt-32 md:mt-40">
            <ChapterHeading num="03" title="Why Kannur TheyyamNow Exists" />
            <div className="mt-12 grid lg:grid-cols-12 gap-14 items-start">
              <motion.div {...reveal} className="lg:col-span-6 md:columns-2 gap-10 text-ivory/70 text-base leading-relaxed space-y-6" data-testid="about-why-exists">
                {whyParagraphs.map((p, i) => (
                  <p key={i} className="break-inside-avoid">{p}</p>
                ))}
              </motion.div>
              <motion.div {...reveal} className="lg:col-span-6">
                <ParallaxImage
                  src={images[1]}
                  alt="Theyyam ritual at the temple"
                  testid="about-image-2"
                  className="aspect-[16/10] w-full"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="audience-section" className="bg-ivory py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ChapterHeading num="04" title="Who This Platform Is For" dark={false} />
          <motion.p {...reveal} className="mt-8 max-w-2xl text-charcoal/70 text-base md:text-lg leading-relaxed">
            Kannur TheyyamNow is built for anyone who wishes to connect with Theyyam — whether you are a devotee, a learner, or a curious explorer.
          </motion.p>

          <div className="mt-16 grid md:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" data-testid="audience-grid">
            {audience.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-ivory p-8 md:p-10 transition-colors duration-500 hover:bg-charcoal"
                data-testid={`audience-item-${i}`}
              >
                <span className="font-serif text-gold text-xl">0{i + 1}</span>
                <p className="mt-6 font-serif text-xl md:text-2xl leading-snug text-charcoal transition-colors duration-500 group-hover:text-ivory">
                  {item}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
